/**
 * Token Error Classifier
 * 
 * Centralizes token error classification for consistent error handling
 * across silent token acquisition paths.
 */

export type TokenErrorKind = 
  | 'interaction_required'
  | 'network'
  | 'invalid_grant'
  | 'system'
  | 'unknown';

export interface TokenErrorClassification {
  kind: TokenErrorKind;
  retryable: boolean;
  userMessage: string;
}

/**
 * Check if error indicates interaction is required
 */
export function isInteractionRequired(error: unknown): boolean {
  const classification = classifyTokenError(error);
  return classification.kind === 'interaction_required';
}

/**
 * Classify token error into structured categories
 * 
 * @param error - Error from token acquisition (MSAL error, network error, etc.)
 * @returns TokenErrorClassification with kind, retryable flag, and user message
 */
export function classifyTokenError(error: unknown): TokenErrorClassification {
  if (!error) {
    return {
      kind: 'unknown',
      retryable: false,
      userMessage: 'An unknown error occurred during authentication.',
    };
  }

  // Handle Error objects
  if (error instanceof Error) {
    const errorMessage = error.message.toLowerCase();
    const errorName = error.name.toLowerCase();

    // Interaction required errors
    if (
      errorMessage.includes('interaction_required') ||
      errorMessage.includes('consent_required') ||
      errorMessage.includes('login_required') ||
      errorName.includes('interaction_required') ||
      errorName.includes('consent_required') ||
      errorName.includes('login_required')
    ) {
      return {
        kind: 'interaction_required',
        retryable: true,
        userMessage: 'Please sign in again to continue.',
      };
    }

    // Invalid grant errors (token expired, invalid, revoked)
    if (
      errorMessage.includes('invalid_grant') ||
      errorMessage.includes('token_expired') ||
      errorMessage.includes('token_invalid') ||
      errorMessage.includes('token_revoked') ||
      errorName.includes('invalid_grant')
    ) {
      return {
        kind: 'invalid_grant',
        retryable: true,
        userMessage: 'Your session has expired. Please sign in again.',
      };
    }

    // Network errors
    if (
      errorMessage.includes('network') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('connection') ||
      errorName.includes('networkerror') ||
      errorName.includes('timeout')
    ) {
      return {
        kind: 'network',
        retryable: true,
        userMessage: 'Network error. Please check your connection and try again.',
      };
    }

    // System errors (server errors, configuration issues)
    if (
      errorMessage.includes('server') ||
      errorMessage.includes('configuration') ||
      errorMessage.includes('internal') ||
      errorName.includes('servererror')
    ) {
      return {
        kind: 'system',
        retryable: true,
        userMessage: 'A system error occurred. Please try again later.',
      };
    }
  }

  // Handle MSAL-specific error objects (may have errorCode, errorMessage properties)
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>;
    
    // Check for MSAL error code
    const errorCode = String(errorObj.errorCode || errorObj.code || '').toLowerCase();
    const errorMessage = String(errorObj.errorMessage || errorObj.message || '').toLowerCase();

    if (
      errorCode.includes('interaction_required') ||
      errorCode.includes('consent_required') ||
      errorCode.includes('login_required') ||
      errorMessage.includes('interaction_required') ||
      errorMessage.includes('consent_required') ||
      errorMessage.includes('login_required')
    ) {
      return {
        kind: 'interaction_required',
        retryable: true,
        userMessage: 'Please sign in again to continue.',
      };
    }

    if (
      errorCode.includes('invalid_grant') ||
      errorMessage.includes('invalid_grant') ||
      errorMessage.includes('token_expired')
    ) {
      return {
        kind: 'invalid_grant',
        retryable: true,
        userMessage: 'Your session has expired. Please sign in again.',
      };
    }
  }

  // Default to unknown
  return {
    kind: 'unknown',
    retryable: false,
    userMessage: 'An unexpected error occurred during authentication. Please try again.',
  };
}
